import { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import ClientFetchQuote from "@/B2C/features/clients/ClientFetchQuote";
import CommissionLedgerPage from "@/B2C/features/CommissionLedger/CommissionLedgerPage";
import InvoicesPage from "@/B2C/features/Invoices/InvoicesPage";
import { App_Url_New } from "@/shared/common/static";
import Modal from "@/shared/components/Modal";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { lazy } from "react";
import AuthLayout from "../B2C/layout/AuthLayout";
import ScrollToTop from "../shared/components/ScrollToTop";
import AppShell from "../shared/layout/AppShell";

export const HomePage = lazy(() => import("@/D2C/pages/home/index"));
export const LoginPage = lazy(() => import("@/D2C/pages/auth/index"));
export const Clients = lazy(() => import("@/B2C/pages/clients/index"));
export const Quotes = lazy(() => import("@/B2C/pages/quotes/index"));
export const clientFetchQuote = lazy(() => import("@/B2C/features/clients/ClientFetchQuote"));
export const ViewPolicyPage = lazy(
  () => import("@/D2C/features/retail-dashboard/components/policies/ViewPolicyPage")
);
export const PoliciesPage = lazy(() => import("@/B2C/features/Policies/PoliciesPage"));

export const PipeLine = lazy(() => import("@/B2C/pages/Pipeline/index"));
export const Settings = lazy(() => import("@/B2C/pages/setting/index"));
export const ApiConfiguration = lazy(() => import("@/ADMIN/pages/ApiConfig/index"));
export const ThirdPartyApi = lazy(() => import("@/ADMIN/pages/ThirdPartyApi/index"));
export const Logs = lazy(() => import("@/ADMIN/pages/Logs-monitoring/index"));

export const ServiceTemplate = lazy(
  () => import("@/ADMIN/feature/apiConfig/components/ServiceTemplatePage")
);
export const Insurer = lazy(() => import("@/ADMIN/pages/insurar/index"));
export const Calls = lazy(() => import("@/B2C/pages/callLogs/index"));
export const Dashboard = lazy(() => import("@/B2C/pages/dashboard/index"));
export const TravelInsurance = lazy(() => import("@/D2C/pages/travel-insurance/index"));
export const CarInsurance = lazy(() => import("@/D2C/pages/car-insurance/index"));

export const HealthInsurance = lazy(() => import("@/D2C/pages/health-insurance/index"));
export const LifeInsurance = lazy(() => import("@/D2C/pages/life-insurance/index"));
export const TravelGetQuote = lazy(() => import("@/D2C/pages/travel-quote/index"));
export const CarGetQuote = lazy(() => import("@/D2C/pages/car-quote/index"));
export const HealthGetQuote = lazy(() => import("@/D2C/pages/health-quote/index"));
export const LifeGetQuote = lazy(() => import("@/D2C/pages/life-quote/index"));

export const PolicyResult = lazy(() => import("@/D2C/pages/policy-result/index"));
export const RetailDashboard = lazy(() => import("@/D2C/pages/retail/dashboard/index"));
export const LovableHub = lazy(() => import("@/B2C/pages/lovable/index"));
export const commissionLedger = lazy(() => import("@/B2C/pages/commissionLedger/index"));

type AppRoute = {
  path: string;
  element: ReactNode;
  module?: string;
  action?: string;
};

// public routes
// D2C routes
const publicRoutes: AppRoute[] = [
  // Home
  { path: App_Url_New.D2CPageUrl.home, element: <HomePage /> },

  {
    // Travel insurance flow
    path: App_Url_New.D2CPageUrl.insurance.travel,
    element: <TravelInsurance />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.travelGetQuote,
    element: <TravelGetQuote />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.travelPolicyResult,
    element: <PolicyResult lob="travel" />,
  },

  // Car insurance flow
  { path: App_Url_New.D2CPageUrl.insurance.car, element: <CarInsurance /> },

  {
    path: App_Url_New.D2CPageUrl.insurance.carGetQuote,
    element: <CarGetQuote />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.carPolicyResult,
    element: <PolicyResult lob="car" />,
  },

  // Health insurance flow
  {
    path: App_Url_New.D2CPageUrl.insurance.health,
    element: <HealthInsurance />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.healthGetQuote,
    element: <HealthGetQuote />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.life,
    element: <LifeInsurance />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.lifeGetQuote,
    element: <LifeGetQuote />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.lifePolicyResult,
    element: <PolicyResult lob="life" />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.healthPolicyResult,
    element: <PolicyResult lob="health" />,
  },

  // Retail dashboard and auth
  {
    path: App_Url_New.D2CPageUrl.retailDashboard,
    element: <RetailDashboard />,
  },

  { path: App_Url_New.CommonPageUrl.login, element: <LoginPage /> },
];

// privates rotes
// B2C routes
const privateRoutes: AppRoute[] = [
  {
    path: App_Url_New.B2CPageUrl.dashboard,
    element: <Dashboard />,
    module: "dashboard",
    action: "view",
  },
  {
    path: App_Url_New.B2CPageUrl.clients,
    element: <Clients />,
    module: "clients",
    action: "list",
  },
  {
    path: App_Url_New.B2CPageUrl.quotes,
    element: <Quotes />,
    module: "quotes",
    action: "view",
  },
  {
    path: App_Url_New.B2CPageUrl.addQuotes,
    element: <Navigate to={`${App_Url_New.B2CPageUrl.addQuotes}/travel`} replace />,
    module: "quotes",
    action: "view",
  },
  {
    path: `${App_Url_New.B2CPageUrl.addQuotes}/:product`,
    element: <Quotes />,
    module: "quotes",
    action: "view",
  },
  {
    path: "/add-quotes",
    element: <Navigate to={`${App_Url_New.B2CPageUrl.addQuotes}/travel`} replace />,
    module: "quotes",
    action: "view",
  },
  {
    path: "/add-quotes/:product",
    element: <Quotes />,
    module: "quotes",
    action: "view",
  },
  {
    path: App_Url_New.B2CPageUrl.editQuote,
    element: <ClientFetchQuote />,
    module: "quotes",
    action: "view",
  },
  {
    path: "/auth/client/quotes",
    element: <ClientFetchQuote />,
    module: "quotes",
    action: "view",
  },
  {
    path: App_Url_New.B2CPageUrl.commissionLedger,
    element: <CommissionLedgerPage />,
    module: "dashboard",
    action: "view",
  },
  {
    path: App_Url_New.B2CPageUrl.invoices,
    element: <InvoicesPage />,
    module: "dashboard",
    action: "view",
  },
  {
    path: App_Url_New.B2CPageUrl.settings,
    element: <Settings />,
    module: "settings",
    action: "view",
  },
  {
    path: App_Url_New.AdminPageUrl.apiConfiguration,
    element: <ApiConfiguration />,
    module: "provider-service",
    action: "list",
  },
  {
    path: App_Url_New.AdminPageUrl.thirdPartyApi,
    element: <ThirdPartyApi />,
    module: "provider-service",
    action: "list",
  },
  {
    path: App_Url_New.AdminPageUrl.insurer,
    element: <Insurer />,
    module: "insurerconfig",
    action: "view",
  },
  {
    path: App_Url_New.AdminPageUrl.apiConfiguration + "/:id" + "/:insurer_id",
    element: <ServiceTemplate />,
    module: "provider-service",
    action: "list",
  },
  {
    path: App_Url_New.AdminPageUrl.logs,
    element: <Logs />,
    module: "logs",
    action: "view",
  },

  {
    path: App_Url_New.B2CPageUrl.calls,
    element: <Calls />,
    module: "calls",
    action: "view",
  },
  {
    path: App_Url_New.B2CPageUrl.pipeline,
    element: <PipeLine />,
    module: "pipeline",
    action: "view",
  },
  {
    path: App_Url_New.B2CPageUrl.policies,
    element: <PoliciesPage />,
    module: "policies",
    action: "view",
  },
  {
    path: `${App_Url_New.B2CPageUrl.policies}/:policyId`,
    element: <ViewPolicyPage />,
    module: "policies",
    action: "view",
  },

  // Lovable iframe routes -> https://insur-spark-dash.lovable.app/<path>
  ...[
    App_Url_New.B2CPageUrl.commissionDashboard,
    // App_Url_New.B2CPageUrl.commissionLedger,
    App_Url_New.B2CPageUrl.invoices,
    App_Url_New.B2CPageUrl.reconciliation,
    App_Url_New.B2CPageUrl.variances,
    App_Url_New.B2CPageUrl.tds,
    App_Url_New.B2CPageUrl.reports,
    App_Url_New.B2CPageUrl.irdaiReports,
    App_Url_New.B2CPageUrl.configuration,
    App_Url_New.B2CPageUrl.cases,
    App_Url_New.B2CPageUrl.claims,
  ].flatMap((basePath) => [
    { path: basePath, element: <LovableHub /> },
    { path: `${basePath}/*`, element: <LovableHub /> },
  ]),
];

export default function AppRouter() {
  const {
    auth: { user, customerAuth },
  } = usePosterReducers();

  const hasUserAccessToken = Boolean(user?.access_token || user?.user?.access_token);
  const hasCustomerAccessToken = Boolean(customerAuth?.access_token);

  const hasPermission = (module?: string, action?: string) => {
    if (!module || !action) return true;
    return user?.user?.role?.permissions?.[module]?.includes(action) ?? false;
  };

  const allowedPrivateRoutes = privateRoutes.filter((route) =>
    hasPermission(route.module, route.action)
  );

  const fallbackPrivatePath = allowedPrivateRoutes[0]?.path || App_Url_New.B2CPageUrl.dashboard;

  const renderRoutes = (routes: AppRoute[]) =>
    routes.map(({ path, element }) => {
      const isLoginRoute = path === App_Url_New.CommonPageUrl.login;
      const loginRedirect = hasCustomerAccessToken
        ? App_Url_New.D2CPageUrl.retailDashboard
        : fallbackPrivatePath;

      return (
        <Route
          key={path}
          path={path}
          element={
            isLoginRoute && (hasUserAccessToken || hasCustomerAccessToken) ? (
              <Navigate to={loginRedirect} replace />
            ) : (
              element
            )
          }
        />
      );
    });

  return (
    <>
      <ScrollToTop />
      <Routes>
        {user ? (
          <>
            <Route element={<AuthLayout />}>{renderRoutes(allowedPrivateRoutes)}</Route>
            <Route path="*" element={<Navigate to={fallbackPrivatePath} replace />} />
          </>
        ) : (
          <>
            <Route element={<AppShell />}>{renderRoutes(publicRoutes)}</Route>
            <Route path="*" element={<Navigate to={App_Url_New.D2CPageUrl.home} replace />} />
          </>
        )}
      </Routes>
      <Modal />
    </>
  );
}
